/**
 * Direct PCM Recording Hook - OPTIMIZED
 * Records audio directly as PCM 16-bit mono (Azure format)
 * Supports configurable sample rates (16kHz for Speech, 24kHz for Realtime)
 * Eliminates WebM→PCM conversion bottleneck (18s → <1s)
 */

import { useState, useRef, useCallback } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { logger } from "@/utils/logger"

interface UseDirectPCMRecorderOptions {
  sampleRate?: number; // 16000 (default, Speech) or 24000 (Realtime)
  onAudioData?: (chunk: Float32Array) => void; // Streaming callback for realtime
}

interface UseDirectPCMRecorderReturn {
  isRecording: boolean;
  recordingTime: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<SpeechSDK.PushAudioInputStream>;
  error: string | null;
}

const MAX_RECORDING_TIME = 120; // 2 minutes

export const useDirectPCMRecorder = (
  options: UseDirectPCMRecorderOptions = {}
): UseDirectPCMRecorderReturn => {
  const { sampleRate = 16000, onAudioData } = options;
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const pushStreamRef = useRef<SpeechSDK.PushAudioInputStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      logger.log("[DirectPCM] Starting direct PCM recording...", {
        sampleRate,
      });

      // 1. Create AudioContext at specified sample rate
      audioContextRef.current = new AudioContext({ sampleRate });
      logger.log("[DirectPCM] AudioContext created:", {
        sampleRate: audioContextRef.current.sampleRate,
      });

      // 2. Request microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      logger.log("[DirectPCM] Microphone access granted");

      // 3. Create Azure PushStream
      const pushStream = SpeechSDK.AudioInputStream.createPushStream(
        SpeechSDK.AudioStreamFormat.getWaveFormatPCM(sampleRate, 16, 1)
      );
      pushStreamRef.current = pushStream;
      logger.log("[DirectPCM] Azure PushStream created");

      // 4. Create audio source and processor
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(
        4096, // Buffer size
        1, // Input channels (mono)
        1 // Output channels
      );
      processorRef.current = processor;

      // 5. Process audio in real-time: Float32 → Int16 PCM → Azure
      processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0); // Float32Array

        // Call streaming callback if provided (for realtime WebSocket)
        if (onAudioData) {
          onAudioData(inputData);
        }

        // Convert Float32 → Int16 PCM in place (super fast)
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Convert Int16 → Uint8 (little-endian)
        const uint8 = new Uint8Array(pcm16.length * 2);
        for (let i = 0; i < pcm16.length; i++) {
          uint8[i * 2] = pcm16[i] & 0xff;
          uint8[i * 2 + 1] = (pcm16[i] >> 8) & 0xff;
        }

        // Stream directly to Azure (no accumulation!)
        pushStream.write(uint8.buffer);
      };

      // 6. Connect: Microphone → Processor → Azure
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      logger.log("[DirectPCM] Audio pipeline connected - streaming started");

      setIsRecording(true);
      setRecordingTime(0);

      // 7. Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= MAX_RECORDING_TIME) {
            logger.log("[DirectPCM] Max time reached, auto-stopping");
            stopRecording();
          }
          return newTime;
        });
      }, 1000);

      logger.log("[DirectPCM] ✅ Recording started successfully");
    } catch (err) {
      logger.error("[DirectPCM] Error starting recording:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Could not access microphone. Please grant permission."
      );
      setIsRecording(false);
    }
  }, [sampleRate, onAudioData]);

  const stopRecording =
    useCallback(async (): Promise<SpeechSDK.PushAudioInputStream> => {
      logger.log("[DirectPCM] Stopping recording...");
      setIsRecording(false);

      try {
        // 1. Disconnect processor
        if (processorRef.current) {
          processorRef.current.disconnect();
          processorRef.current.onaudioprocess = null;
          logger.log("[DirectPCM] Processor disconnected");
        }

        // 2. Stop microphone
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          logger.log("[DirectPCM] Microphone stopped");
        }

        // 3. Close AudioContext
        if (audioContextRef.current) {
          await audioContextRef.current.close();
          logger.log("[DirectPCM] AudioContext closed");
        }

        // 4. Close Azure PushStream (signals end of audio)
        const pushStream = pushStreamRef.current!;
        pushStream.close();
        logger.log("[DirectPCM] ✅ PushStream closed - ready for Azure");

        // 5. Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        return pushStream;
      } catch (err) {
        logger.error("[DirectPCM] Error stopping recording:", err);
        throw err;
      }
    }, []);

  return {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
    error,
  };
};
